import React from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Form,
  Table,
} from "components/ui/BootstrapCompat";
import useLocalStorage from "helpers/useLocalStorage";
import { useMutation, useQuery } from "react-query";
import {
  gameCreateOne,
  gameGetMany,
  gameUpdateOne,
  walletGetMe,
} from "services/game.service";
import { getUserProfileData, userGetMe } from "services/user.service";
import PopUpGamePackage from "components/popup/PopUpGamePackage";
import { ErrorSwal, SuccessSwal } from "helpers/alertsweet";

type Props = {};

const WHATSAPP_SUPPORT_PHONE = "8562078344428";
const TERM_GAME_SUPPORT_MESSAGE =
  "ສະບາຍດີ! ຂ້ອຍຕ້ອງການເປີດໃຊ້ງານ merchant ເຕີມເກມ";

const isTruthyFlag = (value: unknown) =>
  value === true ||
  value === 1 ||
  String(value).trim().toLowerCase() === "true" ||
  String(value).trim().toLowerCase() === "yes";

const isFalseFlag = (value: unknown) =>
  value === false ||
  value === 0 ||
  String(value).trim().toLowerCase() === "false" ||
  String(value).trim().toLowerCase() === "no";

export default function GamePage({}: Props) {
  const [popup, setPopup] = React.useState<any>({});
  const [searchTerm, setSearchTerm] = React.useState("");
  const [page, setPage] = React.useState(1);
  const emptyGameForm = {
    _id: "",
    name: "",
    category: "game",
    active: "PUBLIC",
    image: "",
    banner: "",
    description: "",
    sort: "",
  };
  const [gameForm, setGameForm] = React.useState<any>(emptyGameForm);
  const [token] = useLocalStorage("token", "");
  const [businessId] = useLocalStorage("businessId", "");

  const PAGE_SIZE = 50;

  const { data: authMeResponse, isLoading: isAuthMeLoading } = useQuery(
    ["game-auth-me", businessId || "self"],
    () => userGetMe({ token: token || "", businessId: businessId || "" }),
    {
      enabled: !!token,
      retry: false,
    },
  );
  const authMeData = getUserProfileData(authMeResponse);
  const termgameMerchant = authMeData?.termgameMerchant || {};
  const merchantAvailableValue =
    authMeData?.merchantAvailable ??
    authMeData?.termgameMerchantAvailable ??
    termgameMerchant?.merchantAvailable ??
    termgameMerchant?.available;
  const isTermgameMerchantAvailable = !isFalseFlag(merchantAvailableValue);
  const termgameBusinessId =
    isTermgameMerchantAvailable
      ? termgameMerchant?.businessId ||
        termgameMerchant?.identify ||
        authMeData?.termgameBusinessId ||
        ""
      : "";
  const termgameMerchantStatus = String(
    authMeData?.termgameMerchantStatus ||
      termgameMerchant?.status ||
      (isTruthyFlag(termgameMerchant?.active) ? "ACTIVE" : ""),
  ).toUpperCase();
  const hasExplicitTermgameStatus = Boolean(termgameMerchantStatus);
  const isTermgameMerchantReady =
    isTermgameMerchantAvailable &&
    (!hasExplicitTermgameStatus ||
      ["ACTIVE", "APPROVED", "ENABLED", "PUBLIC"].includes(
        termgameMerchantStatus,
      ));
  const shouldShowTermgameActivationCard =
    !isAuthMeLoading && (!termgameBusinessId || !isTermgameMerchantReady);
  const termgameActivationMessage =
    authMeData?.termgameMerchantMessage ||
    termgameMerchant?.message ||
    "merchant ເຕີມເກມຂອງທ່ານຍັງບໍ່ໄດ້ເປີດໃຊ້ງານ";
  const termgameSupportUrl = `https://wa.me/${WHATSAPP_SUPPORT_PHONE}?text=${encodeURIComponent(
    TERM_GAME_SUPPORT_MESSAGE,
  )}`;

  // get wallet credit
  const { data: walletData } = useQuery(
    ["game-wallet-me", termgameBusinessId],
    async () => {
      try {
        const response = await walletGetMe({
          token: token || "",
          businessId: termgameBusinessId,
        });
        return response;
      } catch (error: any) {
        console.error("Failed to fetch wallet:", error);
        return null;
      }
    },
    {
      enabled: !!token && !!termgameBusinessId && isTermgameMerchantReady,
    },
  );

  // get data
  const { data, isLoading, isRefetching, refetch } = useQuery(
    ["game-list", termgameBusinessId],
    async () => {
      try {
        const response = await gameGetMany({
          findby: {},
          token: token || "",
          limit: 50,
          businessId: termgameBusinessId,
        });
        return response;
      } catch (error: any) {
        console.error("Failed to fetch games:", error);
        return [];
      }
    },
    {
      enabled: !!token && !!termgameBusinessId && isTermgameMerchantReady,
    },
  );

  const filteredData = data?.filter((game: any) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      game?.name?.toLowerCase().includes(search) ||
      game?.category?.toLowerCase().includes(search) ||
      (game?.recommended ? "recommended ແນະນຳ" : "")
        .toLowerCase()
        .includes(search) ||
      game?._id?.toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil((filteredData?.length || 0) / PAGE_SIZE);
  const pagedData = filteredData?.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("lo-LA").format(amount);
  };

  const getFirstNumber = (source: any, keys: string[]) => {
    for (const key of keys) {
      const value = Number(source?.[key]);
      if (Number.isFinite(value)) return value;
    }
    return 0;
  };

  const getFirstString = (source: any, keys: string[]) => {
    for (const key of keys) {
      const value = String(source?.[key] || "").trim();
      if (value) return value;
    }
    return "";
  };

  const isActiveGame = (game: any) =>
    game?.active === "PUBLIC" || game?.status === "active";

  const isRecommendedGame = (game: any) =>
    game?.recommended === true || game?.isRecommended === true;

  const renderSwitch = ({
    checked,
    disabled,
    onChange,
    label,
  }: {
    checked: boolean;
    disabled: boolean;
    onChange: () => void;
    label: string;
  }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-[26px] w-[48px] flex-shrink-0 items-center rounded-full border transition ${
        checked
          ? "border-primary bg-primary"
          : "border-gray-300 bg-gray-200 dark:border-slate-700 dark:bg-slate-800"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-[20px] w-[20px] rounded-full bg-white shadow transition ${
          checked ? "translate-x-[23px]" : "translate-x-[3px]"
        }`}
      />
    </button>
  );

  const gameRevenueAmount = getFirstNumber(walletData, [
    "gameRevenue",
    "termgameRevenue",
    "topupRevenue",
    "totalRevenue",
    "totalAmount",
    "balance",
  ]);
  const displayedGameRevenueAmount =
    getFirstNumber(termgameMerchant, [
      "gameRevenue",
      "termgameRevenue",
      "topupRevenue",
      "totalRevenue",
      "totalAmount",
      "balance",
    ]) || gameRevenueAmount;
  const ownGameWebsiteUrl = getFirstString(walletData, [
    "gameWebsiteUrl",
    "termgameWebsiteUrl",
    "topupWebsiteUrl",
    "publicGameUrl",
    "publicTopupUrl",
    "websiteUrl",
    "publicUrl",
  ]);
  const displayedGameWebsiteUrl =
    getFirstString(termgameMerchant, [
      "gameWebsiteUrl",
      "termgameWebsiteUrl",
      "url",
      "websiteUrl",
      "publicUrl",
    ]) || ownGameWebsiteUrl;
  const gameWebsiteDescription =
    getFirstString(termgameMerchant, [
      "gameWebsiteDescription",
      "termgameWebsiteDescription",
      "websiteDescription",
      "message",
    ]) ||
    getFirstString(walletData, [
      "gameWebsiteDescription",
      "termgameWebsiteDescription",
      "topupWebsiteDescription",
      "websiteDescription",
    ]) || "ໃຊ້ link ນີ້ເພື່ອແຊຣ໌ໜ້າເວັບເຕີມເກມຂອງທ່ານ";

  const copyText = async (text: string, successMessage: string) => {
    if (!text) {
      ErrorSwal("ບໍ່ພົບ link ສຳລັບ copy");
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      SuccessSwal(successMessage);
    } catch (error) {
      ErrorSwal("copy link ບໍ່ສຳເລັດ");
    }
  };
  const resetGameForm = () => setGameForm(emptyGameForm);

  const saveGameMutation = useMutation(
    async () => {
      if (!gameForm.name.trim()) throw new Error("ກະລຸນາປ້ອນຊື່ເກມ");

      const body = {
        name: gameForm.name.trim(),
        category: gameForm.category,
        active: gameForm.active,
        status: gameForm.active === "PUBLIC" ? "active" : "inactive",
        image: gameForm.image.trim(),
        banner: gameForm.banner.trim(),
        description: gameForm.description.trim(),
        sort: gameForm.sort === "" ? undefined : Number(gameForm.sort),
      };

      return gameForm._id
        ? await gameUpdateOne(
            gameForm._id,
            body,
            token || "",
            termgameBusinessId,
          )
        : await gameCreateOne(body, token || "", termgameBusinessId);
    },
    {
      onSuccess: async (res: any) => {
        if (res?.error) {
          ErrorSwal(res?.message || "ບັນທຶກເກມບໍ່ສຳເລັດ");
          return;
        }
        SuccessSwal("ບັນທຶກເກມສຳເລັດ");
        resetGameForm();
        await refetch();
      },
      onError: (error: any) => {
        ErrorSwal(error?.message || "ບັນທຶກເກມບໍ່ສຳເລັດ");
      },
    },
  );

  const updateGameStatusMutation = useMutation(
    async ({
      game,
      body,
    }: {
      game: any;
      body: Record<string, string | boolean>;
    }) =>
      await gameUpdateOne(
        game._id,
        {
          name: game?.name,
          category: game?.category || "game",
          image: game?.image || "",
          banner: game?.banner || "",
          description: game?.description || "",
          sort: game?.sort,
          ...body,
        },
        token || "",
        termgameBusinessId,
      ),
    {
      onSuccess: async (res: any) => {
        if (res?.error) {
          ErrorSwal(res?.message || "ອັບເດດເກມບໍ່ສຳເລັດ");
          return;
        }
        SuccessSwal("ອັບເດດເກມສຳເລັດ");
        await refetch();
      },
      onError: (error: any) => {
        ErrorSwal(error?.message || "ອັບເດດເກມບໍ່ສຳເລັດ");
      },
    },
  );

  const editGame = (game: any) => {
    setGameForm({
      _id: game?._id || "",
      name: game?.name || "",
      category: game?.category || "game",
      active:
        game?.active || (game?.status === "active" ? "PUBLIC" : "PRIVATE"),
      image: game?.image || "",
      banner: game?.banner || "",
      description: game?.description || "",
      sort: game?.sort ?? "",
    });
  };

  const toggleGameActive = (game: any) => {
    const nextActive = isActiveGame(game) ? "PRIVATE" : "PUBLIC";
    updateGameStatusMutation.mutate({
      game,
      body: {
        active: nextActive,
        status: nextActive === "PUBLIC" ? "active" : "inactive",
      },
    });
  };

  const toggleRecommendedGame = (game: any) => {
    updateGameStatusMutation.mutate({
      game,
      body: {
        recommended: !isRecommendedGame(game),
        isRecommended: !isRecommendedGame(game),
      },
    });
  };

  if (shouldShowTermgameActivationCard) {
    return (
      <div className="min-w-0">
        <Breadcrumb>
          <Breadcrumb.Item active>ເຄື່ອງມືພື້ນຖານ</Breadcrumb.Item>
          <Breadcrumb.Item active>ລາຍການເກມ</Breadcrumb.Item>
        </Breadcrumb>

        <Card border="warning">
          <Card.Header>ຍັງບໍ່ເປີດໃຊ້ງານ merchant ເຕີມເກມ</Card.Header>
          <Card.Body>
            <Alert variant="warning" className="mb-3">
              {termgameActivationMessage}
            </Alert>
            <div className="mb-3 text-[13px] leading-6 text-[#475467] dark:text-slate-300">
              ຟີເຈີນີ້ສຳລັບ creator ທີ່ໄດ້ເປີດ merchant ເຕີມເກມແລ້ວ.
              ຫຼັງຈາກ admin ເປີດສິດໃຫ້ ໜ້ານີ້ຈະສະແດງ link
              ເວັບເຕີມເກມ, ລາຍໄດ້, ແລະລາຍການເກມທີ່ຈັດການໄດ້.
            </div>
            <a
              href={termgameSupportUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
            >
              ຕິດຕໍ່ admin
            </a>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <Breadcrumb>
        <Breadcrumb.Item active>ເຄື່ອງມືພື້ນຖານ</Breadcrumb.Item>
        <Breadcrumb.Item active>ລາຍການເກມ</Breadcrumb.Item>
      </Breadcrumb>

      <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        <Card>
          <Card.Header>ເກມທັງໝົດ</Card.Header>
          <Card.Body>
            <div>
              <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
                {data?.length || 0}
                <span className="text-[14px]">ເກມ</span>
              </h1>
              <div className="text-gray-600 text-[12px]">
                ກຳລັງໃຊ້ງານ{" "}
                <span className="text-primary">
                  {data?.filter((game: any) => isActiveGame(game)).length || 0}
                </span>{" "}
                ເກມ
              </div>
              <div className="text-gray-600 text-[12px]">
                ເກມແນະນຳ{" "}
                <span className="text-primary">
                  {data?.filter((game: any) => isRecommendedGame(game))
                    .length || 0}
                </span>{" "}
                ເກມ
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ຂໍ້ມູນເວັບເຕີມເກມ</Card.Header>
          <Card.Body>
            <div className="rounded border border-[#dbe3ec] bg-white p-3 text-[12px] text-[#475467] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
              <div className="mb-2 font-bold text-[#101828] dark:text-slate-100">
                Link ເວັບຂອງທ່ານ
              </div>
              {displayedGameWebsiteUrl ? (
                <button
                  type="button"
                  onClick={() =>
                    copyText(
                      displayedGameWebsiteUrl,
                      "copy link ເວັບສຳເລັດ",
                    )
                  }
                  className="max-w-full break-all text-left text-[12px] font-bold text-primary-500 underline"
                >
                  {displayedGameWebsiteUrl}
                </button>
              ) : (
                <div className="font-bold text-[#101828] dark:text-slate-100">
                  ລໍຖ້າ backend ສົ່ງ link ເວັບ
                </div>
              )}
              <div className="mt-2">{gameWebsiteDescription}</div>
            </div>
            <div className="mt-2 text-[11px] text-gray-500">
              ຈຳນວນເກມທີ່ເປີດ:{" "}
              {data?.filter((game: any) => isActiveGame(game)).length || 0} ເກມ
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>ລາຍໄດ້ຈາກເວັບເຕີມເກມ</Card.Header>
          <Card.Body>
            <h1 className="break-words text-[28px] leading-tight sm:text-[36px] xl:text-[44px]">
              {formatCurrency(displayedGameRevenueAmount)}
              <span className="text-[14px]">ກີບ</span>
            </h1>
            <div className="mt-2 text-[12px] text-gray-600">
              ລາຍໄດ້ລວມຈາກ order/topup ຂອງເວັບເຕີມເກມ
            </div>
            <Link
              to="/game/conditions"
              className="mt-2 inline-flex text-[12px] font-bold text-primary-500 underline"
            >
              ເບິ່ງເງື່ອນໄຂ
            </Link>
          </Card.Body>
        </Card>
      </div>

      <div className="mb-2 flex flex-wrap gap-2 rounded bg-white p-2">
        <Form.Control
          type="text"
          placeholder="ຄົ້ນຫາ..."
          className="min-w-0 flex-[1_1_100%] border border-primary md:flex-none md:w-[200px]"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
        />
        <Button
          variant="primary"
          className="flex flex-1 items-center sm:flex-none"
        >
          <span className="material-icons">search</span>
        </Button>
        <Button
          variant="outline-primary"
          className="flex-1 sm:flex-none"
          onClick={() => refetch()}
        >
          Refetch
        </Button>
        <div className="hidden flex-1 md:block" />
      </div>

      <div>
        <Card border="primary">
          <Card.Header className="p-1">ລາຍການເກມ</Card.Header>
          <Table striped bordered hover size="sm" className="min-w-[600px]">
            <thead>
              <tr>
                <th className="py-2 px-3 text-left border-b w-[60px]">ລ/ດ</th>
                <th className="py-2 px-3 text-left border-b">ຊື່ເກມ</th>
                <th className="py-2 px-3 text-center border-b">ສະຖານະ</th>
                <th className="py-2 px-3 text-center border-b">ແນະນຳ</th>
                <th className="py-2 px-3 text-right border-b">ຈັດການ</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isRefetching ? (
                <tr>
                  <td colSpan={5} className="py-4 text-center">
                    <div className="flex justify-center items-center">
                      <span>ກຳລັງໂຫລດຂໍ້ມູນ...</span>
                    </div>
                  </td>
                </tr>
              ) : pagedData && pagedData.length > 0 ? (
                pagedData.map((game: any, i: number) => (
                  <tr key={game._id}>
                    <td className="py-2 px-3 border-b">
                      {(page - 1) * PAGE_SIZE + i + 1}
                    </td>
                    <td className="py-2 px-3 border-b">
                      <div className="flex flex-col gap-1">
                        <div className="font-bold text-[16px] text-primary">
                          {game?.name || "--"}
                        </div>
                        <div className="text-[10px] text-neutral-500">
                          ID: {game?._id}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3 border-b text-center">
                      {renderSwitch({
                        checked: isActiveGame(game),
                        disabled: updateGameStatusMutation.isLoading,
                        onChange: () => toggleGameActive(game),
                        label: "ເປີດ ຫຼື ປິດເກມ",
                      })}
                    </td>
                    <td className="py-2 px-3 border-b text-center">
                      {renderSwitch({
                        checked: isRecommendedGame(game),
                        disabled: updateGameStatusMutation.isLoading,
                        onChange: () => toggleRecommendedGame(game),
                        label: "ເປີດ ຫຼື ປິດເກມແນະນຳ",
                      })}
                    </td>

                    <td className="py-2 px-3 border-b text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() =>
                            setPopup({ PopUpGamePackage: true, game })
                          }
                        >
                          ແພັກເກດ
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-4 text-center">
                    ບໍ່ພົບຂໍ້ມູນ
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="flex flex-col gap-2 border-t bg-white px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-500">
              ທັງໝົດ {filteredData?.length || 0} ລາຍການ
            </div>
            <div className="flex max-w-full items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={page === 1}
                onClick={() => setPage(1)}
              >
                «
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2,
                )
                .reduce<(number | string)[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1)
                    acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === "..." ? (
                    <span key={`ellipsis-${idx}`} className="px-2">
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={`btn btn-sm ${page === p ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setPage(p as number)}
                    >
                      {p}
                    </button>
                  ),
                )}
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={page === totalPages || totalPages === 0}
                onClick={() => setPage((p) => p + 1)}
              >
                ›
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={page === totalPages || totalPages === 0}
                onClick={() => setPage(totalPages)}
              >
                »
              </button>
            </div>
          </div>
        </Card>
      </div>

      <PopUpGamePackage
        game={popup.game}
        businessId={termgameBusinessId}
        open={!!popup.PopUpGamePackage}
        onClose={() => setPopup({})}
      />
    </div>
  );
}
